import type {
  BridgeCallback,
  BridgeEventType,
  BridgeRequest,
  EventLogging,
  GetAccessToken,
  GetImage,
  OpenDialogRequest,
  OpenUrl,
  SetHistoryBackBlock,
  SetMainTabBarVisibility,
  WebViewMessage,
  NativeResponse,
} from './types';
import { BridgeEvent, AccessTokenResponseType, EnvType } from './types';

export type BridgeApiConstructorType = {
  devDebugConsole: (value: string) => void;
  ENV: EnvType;
  setAccessToken: (token: string | null, response?: AccessTokenResponseType) => void;
  sendMessageHandler: (message: string) => Promise<void>; // Platform-specific message sender
};

export class BridgeApi {
  private devDebugConsole: (value: string) => void;
  private ENV: EnvType;
  private setAccessToken: (token: string | null, response?: AccessTokenResponseType) => void;
  private sendMessageHandler: (message: string) => Promise<void>;
  private requestId: number = 0;
  private pendingRequests: Map<string, { resolve: Function; reject: Function }> = new Map();

  constructor({ devDebugConsole, ENV, setAccessToken, sendMessageHandler }: BridgeApiConstructorType) {
    this.devDebugConsole = devDebugConsole;
    this.ENV = ENV;
    this.setAccessToken = setAccessToken;
    this.sendMessageHandler = sendMessageHandler;
  }

  /**
   * Opens a URL in the native browser or webview
   */
  public openUrl(data: OpenUrl): void {
    this.sendData<OpenUrl>({
      name: 'openUrl',
      data: data,
    });
  }

  /**
   * Triggers native back navigation
   */
  public goBack(): void {
    this.sendData({ name: 'goBack' });
  }

  /**
   * Reloads the current webview
   */
  public reload(): void {
    this.sendData({ name: 'reload' });
  }

  /**
   * Opens a native dialog and returns the user's response
   */
  public async openDialog(data: OpenDialogRequest): Promise<string> {
    const requestId = this.generateRequestId();
    const eventPromise = this.createCustomEvent(BridgeEvent.openDialog, requestId);

    await this.sendData({
      name: 'openDialog',
      data: { ...data, requestId },
    });

    return eventPromise;
  }

  /**
   * Controls the visibility of the main tab bar
   */
  public setMainTabBarVisibility(data: SetMainTabBarVisibility): void {
    this.sendData<SetMainTabBarVisibility>({ name: 'setMainTabBarVisibility', data });
  }

  /**
   * Opens native image picker and returns selected images as base64
   */
  public async getImage(data: GetImage, callback?: BridgeCallback): Promise<string[]> {
    const requestId = this.generateRequestId();
    const promise = this.createCustomEvent(BridgeEvent.getImage, requestId);

    await this.sendData(
      {
        name: 'getImage',
        data: { ...data, requestId },
      },
      callback,
    );

    return promise;
  }

  /**
   * Retrieves the current access token from native storage
   */
  public async getAccessToken(data?: GetAccessToken): Promise<string | null> {
    const requestId = this.generateRequestId();
    const promise = this.createCustomEvent(BridgeEvent.getAccessToken, requestId);

    await this.sendData(
      {
        name: 'getAccessToken',
        data: { ...data, requestId },
      },
      {
        onSuccess: 'setAccessToken',
      },
    );

    return promise;
  }

  /**
   * Controls whether the hardware back button is blocked
   */
  public setHistoryBackBlock(data: SetHistoryBackBlock): void {
    this.sendData({
      name: 'setHistoryBackBlock',
      data: data,
    });
  }

  /**
   * Opens the native login dialog
   */
  public async openLoginDialog(): Promise<void> {
    await this.sendData({
      name: 'openLoginDialog',
      data: {
        referrer: 'community',
      },
    });
  }

  /**
   * Logs events to native analytics (only in production)
   */
  public async eventLogging(data: EventLogging): Promise<void> {
    if (this.ENV === EnvType.PROD) {
      await this.sendData({ name: 'eventLogging', data });
    }
  }

  /**
   * Handles messages received from the native side
   */
  public handleNativeMessage(message: WebViewMessage): void {
    const { type, data, requestId } = message;

    switch (type) {
      case 'dialogResponse':
        this.resolveRequest(requestId!, data);
        break;

      case 'imageResponse':
        if (data && Array.isArray(data)) {
          // For web, data might already be base64
          // For mobile, this would be handled by the mobile-specific implementation
          this.resolveRequest(requestId!, data);
        } else {
          this.rejectRequest(requestId!, new Error('Invalid image data received'));
        }
        break;

      case 'accessTokenResponse':
        if (data?.token) {
          this.setAccessToken(data.token, AccessTokenResponseType.SUCCESS);
          this.resolveRequest(requestId!, data.token);
        } else {
          this.setAccessToken(null, AccessTokenResponseType.ERROR);
          this.resolveRequest(requestId!, null);
        }
        break;

      case 'error':
        this.rejectRequest(requestId!, new Error(data?.message || 'Unknown error'));
        break;

      default:
        this.devDebugConsole(`Unknown message type: ${type}`);
    }
  }

  /**
   * Creates a promise that resolves when a specific event is received
   */
  private createCustomEvent(eventType: BridgeEventType, requestId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.pendingRequests.set(requestId, { resolve, reject });

      // Set a timeout to prevent hanging promises
      setTimeout(() => {
        if (this.pendingRequests.has(requestId)) {
          this.pendingRequests.delete(requestId);
          reject(new Error(`Bridge request timeout for ${eventType}`));
        }
      }, 30000); // 30 second timeout
    });
  }

  /**
   * Resolves a pending request
   */
  private resolveRequest(requestId: string, data: any): void {
    const request = this.pendingRequests.get(requestId);
    if (request) {
      this.pendingRequests.delete(requestId);
      request.resolve(data);
    }
  }

  /**
   * Rejects a pending request
   */
  private rejectRequest(requestId: string, error: Error): void {
    const request = this.pendingRequests.get(requestId);
    if (request) {
      this.pendingRequests.delete(requestId);
      request.reject(error);
    }
  }

  /**
   * Generates a unique request ID
   */
  private generateRequestId(): string {
    return `bridge_${Date.now()}_${++this.requestId}`;
  }

  /**
   * Dispatches a custom event (for web platform)
   */
  public dispatchCustomEvent<T>(eventType: BridgeEventType, value: T): void {
    if (typeof window !== 'undefined') {
      const event = new CustomEvent(eventType, { detail: value });
      window.dispatchEvent(event);
    }
  }

  /**
   * Creates a custom event listener (for web platform)
   */
  public createCustomEventListener(eventType: BridgeEventType): Promise<any> {
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('Window is not available'));
    }

    return new Promise((resolve) => {
      const listener = (event: CustomEvent) => {
        this.devDebugConsole(JSON.stringify(event));
        resolve(event.detail);
        window.removeEventListener(eventType, listener as EventListener);
      };
      window.addEventListener(eventType, listener as EventListener, { once: true });
    });
  }

  /**
   * Sends data to the platform-specific handler
   */
  private async sendData<T>(data: BridgeRequest<T>, callback?: BridgeCallback): Promise<void> {
    const message: WebViewMessage = {
      type: data.name,
      data: data.data,
      requestId: (data.data as any)?.requestId,
    };

    const messageString = JSON.stringify({ ...message, ...callback });
    this.devDebugConsole(`Sending message: ${messageString}`);
    await this.sendMessageHandler(messageString);
  }
}
